import { RedirectRequest } from '@azure/msal-browser';
import { ClientConfig } from '../../clientConfig';
import { OPENID_PARAMS } from '../../types/openid_enum';

type BuildAuthenticationRequestParams = {
    brandInfo: ClientConfig['brandConfig'];
    correlationId: string;
    scopes: string[];
    searchParams: URLSearchParams;
};

export type BuildBaseAuthenticationRequestParams = BuildAuthenticationRequestParams;

const ALLOWED_PROMPT_VALUES = ['login', 'select_account', 'consent', 'none', 'create'] as const;
type AllowedPromptValue = (typeof ALLOWED_PROMPT_VALUES)[number];

function getAllowedPromptValue(prompt: string | null): AllowedPromptValue | undefined {
    if (!prompt) {
        return undefined;
    }

    return ALLOWED_PROMPT_VALUES.includes(prompt as AllowedPromptValue) ? (prompt as AllowedPromptValue) : undefined;
}

/**
 * Builds the shared MSAL redirect request fields used by PIP authentication flows.
 */
export function buildBaseAuthenticationRequest({ brandInfo, correlationId, scopes, searchParams }: BuildBaseAuthenticationRequestParams): RedirectRequest {
    const language = searchParams.get(OPENID_PARAMS.LANG) || 'en';
    const ehrSystem = searchParams.get(OPENID_PARAMS.EHR_SYSTEM) as string;
    const cancelRedirectUrl = searchParams.get(OPENID_PARAMS.CANCEL_REDIRECT_URI) || '';
    const loginHint = searchParams.get('login_hint') || undefined;
    const state = searchParams.get(OPENID_PARAMS.STATE) || undefined;
    const msalRedirectRequest: RedirectRequest = {
        scopes,
        correlationId,
        extraQueryParameters: {
            brand: brandInfo.brand,
            lang: language,
            [OPENID_PARAMS.CANCEL_REDIRECT_URI]: cancelRedirectUrl,
            [OPENID_PARAMS.DISPLAY_MFA_FLOWS]: searchParams.get(OPENID_PARAMS.DISPLAY_MFA_FLOWS) || 'false',
            [OPENID_PARAMS.CORRELATION_ID]: correlationId,
        },
    };

    if (msalRedirectRequest.extraQueryParameters && ehrSystem) {
        msalRedirectRequest.extraQueryParameters[OPENID_PARAMS.EHR_SYSTEM] = ehrSystem;
    }
    if (msalRedirectRequest.extraQueryParameters) {
        msalRedirectRequest.extraQueryParameters.cssbranding = brandInfo.cssbranding;
    }
    if (loginHint) {
        msalRedirectRequest.loginHint = loginHint;
    }
    if (state) {
        msalRedirectRequest.state = state;
    }

    return msalRedirectRequest;
}

/**
 * Builds the default MSAL redirect request from PIP's current URL parameters.
 */
export function buildAuthenticationRequest(params: BuildAuthenticationRequestParams): RedirectRequest {
    const msalRedirectRequest = buildBaseAuthenticationRequest(params);
    const prompt = getAllowedPromptValue(params.searchParams.get('prompt'));

    if (prompt) {
        msalRedirectRequest.prompt = prompt;
    }

    return msalRedirectRequest;
}
